/**
 * Typical entry-level job adverts, for a check run without a real one.
 *
 * Most people who search for an ATS checker have a CV and no advert in front of them.
 * The score is always a match against a job, so rather than turn them away, they pick
 * the role they are aiming for and the box fills with an advert of the usual shape for
 * it — visible and editable, so it is plain what the CV is being scored against.
 *
 * Each one is written the way Indian job portals list these roles for freshers and
 * people with up to two years' experience: what the job does, then what it asks for.
 */
export interface JobPreset {
  id: string;
  role: string;
  text: string;
}

export const JOB_PRESETS: JobPreset[] = [
  {
    id: 'software-developer',
    role: 'Software developer',
    text: `Software Developer (0–2 years)

Responsibilities:
- build and maintain features in web applications using JavaScript, React and Node.js, or Java and Spring Boot
- write clean, tested code and take part in code reviews
- design and consume REST APIs and work with SQL and NoSQL databases such as MySQL, PostgreSQL and MongoDB
- debug production issues and improve performance
- work in an Agile team with product managers and designers

Requirements:
- a degree in computer science, IT or a related field
- strong fundamentals in data structures, algorithms and object-oriented programming
- hands-on experience with at least one language: Java, Python, JavaScript or C++
- familiarity with Git, HTML, CSS and REST APIs
- projects or internships that show you have built and shipped working software
- good problem-solving and communication skills`,
  },
  {
    id: 'data-analyst',
    role: 'Data analyst',
    text: `Data Analyst (0–2 years)

Responsibilities:
- collect, clean and analyse data from multiple sources to answer business questions
- build dashboards and reports in Power BI or Tableau for business teams
- write SQL queries to extract and join data from relational databases
- use Excel (pivot tables, VLOOKUP, XLOOKUP) and Python (pandas, NumPy) for analysis
- track KPIs, find trends and present insights to stakeholders

Requirements:
- graduate in any discipline, with a head for numbers
- strong SQL and advanced Excel skills
- experience with Power BI or Tableau
- working knowledge of Python or R for data analysis
- understanding of basic statistics and data visualisation
- clear written and verbal communication; able to explain findings to non-technical teams`,
  },
  {
    id: 'digital-marketing',
    role: 'Digital marketing',
    text: `Digital Marketing Executive (0–2 years)

Responsibilities:
- plan and run campaigns across Google Ads, Meta (Facebook and Instagram) Ads and LinkedIn
- manage social media content calendars and grow engagement and followers
- do on-page and off-page SEO, keyword research and content optimisation
- run email marketing campaigns and track open and click rates
- measure performance in Google Analytics 4 and report on leads, CTR, CPC and ROI

Requirements:
- graduate in any discipline; a marketing or communications background helps
- hands-on experience with Google Ads, Meta Ads Manager and Google Analytics
- knowledge of SEO, SEM, social media marketing and content marketing
- familiarity with Canva, WordPress and email tools such as Mailchimp
- certifications such as Google Ads or HubSpot are a plus
- strong writing, creativity and an analytical approach to results`,
  },
  {
    id: 'sales',
    role: 'Sales / BDE',
    text: `Business Development Executive – Sales (0–2 years)

Responsibilities:
- generate leads through cold calling, email, LinkedIn and field visits
- pitch products to prospective clients, run demos and handle objections
- build and manage a sales pipeline in a CRM such as Salesforce, Zoho or HubSpot
- achieve monthly and quarterly sales targets and revenue goals
- negotiate, close deals and maintain long-term client relationships
- prepare sales reports and share market feedback with the team

Requirements:
- graduate in any discipline
- excellent communication and negotiation skills in English and Hindi
- experience in B2B or B2C sales, inside sales or an internship in sales
- comfortable with targets, follow-ups and field work
- working knowledge of Excel and CRM tools
- self-motivated, persuasive and customer-focused`,
  },
  {
    id: 'accounts',
    role: 'Accounts executive',
    text: `Accounts Executive (0–2 years)

Responsibilities:
- record day-to-day transactions and maintain books of accounts in Tally Prime
- prepare and file GST returns (GSTR-1, GSTR-3B) and handle TDS deductions and returns
- perform bank reconciliation and vendor and customer ledger reconciliation
- manage accounts payable and accounts receivable, invoicing and payment follow-ups
- support month-end closing, audits and preparation of financial statements

Requirements:
- B.Com or an equivalent commerce degree
- working knowledge of Tally Prime, GST, TDS and accounting principles
- advanced Excel: pivot tables, VLOOKUP and formulas
- knowledge of journal entries, ledgers, trial balance and balance sheet
- attention to detail and accuracy with numbers
- good communication and time management skills`,
  },
  {
    id: 'hr',
    role: 'HR executive',
    text: `HR Executive – Recruitment (0–2 years)

Responsibilities:
- source candidates through Naukri, LinkedIn, job portals, referrals and campus drives
- screen resumes, conduct phone screening and schedule interviews
- coordinate with hiring managers on requirements and interview feedback
- manage offer letters, onboarding and joining formalities
- maintain employee records and HR MIS reports in Excel and the HRMS
- support employee engagement activities, attendance and payroll inputs

Requirements:
- graduate in any discipline, with HR experience or an HR internship
- understanding of end-to-end recruitment and the hiring lifecycle
- familiarity with job portals, applicant tracking systems and HRMS tools
- basic knowledge of labour laws, PF and ESIC is a plus
- good Excel, communication and interpersonal skills
- organised, discreet and comfortable handling multiple positions at once`,
  },
  {
    id: 'customer-support',
    role: 'Customer support',
    text: `Customer Support Executive (0–2 years)

Responsibilities:
- handle customer queries over calls, chat and email in English and Hindi
- resolve complaints and issues within agreed turnaround times and SLAs
- log every interaction and ticket in the CRM or helpdesk tool such as Freshdesk or Zendesk
- escalate complex issues to the right team and follow up until closed
- maintain high customer satisfaction (CSAT) and quality scores

Requirements:
- graduate or 12th pass in any stream; freshers welcome
- excellent spoken and written communication in English and Hindi
- basic computer skills, typing speed and MS Office
- patience, empathy and a problem-solving attitude
- willing to work in rotational shifts
- prior experience in BPO, call centre or customer service is a plus`,
  },
  {
    id: 'graphic-designer',
    role: 'Graphic designer',
    text: `Graphic Designer (0–2 years)

Responsibilities:
- design social media posts, banners, brochures, presentations and ad creatives
- create brand-consistent visuals and follow brand guidelines
- work with the marketing team to turn briefs into finished designs on deadline
- edit images and prepare files for print and digital use
- create simple motion graphics and short video edits for social media

Requirements:
- a degree or diploma in design, or equivalent experience
- strong skills in Adobe Photoshop, Illustrator and InDesign; Figma and Canva are a plus
- good sense of typography, colour, layout and composition
- basic knowledge of After Effects or Premiere Pro is a plus
- A portfolio that shows your design work
- creative, detail-oriented and able to take feedback`,
  },
];
